import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Plug, RefreshCw, Store, AlertCircle, Trash2, Pencil, KeyRound, Package, ScrollText, ShoppingCart, Upload, RotateCcw, Tag, LayoutDashboard, BarChart3 } from "lucide-react";
import { fmtDate, fmtTRY, fmtQty4  } from "../../lib/format";
import { errText } from "../../lib/errors";
import MediaPicker from "../../components/MediaPicker";

const tl = fmtTRY;

const inputCls = "w-full h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";
const lbl = "text-xs font-semibold text-stone-600";

const STATUS_UI = {
  active: { label: "Aktif", cls: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  pending_auth: { label: "Yetkilendirme Bekleniyor", cls: "bg-amber-100 text-amber-800", dot: "bg-amber-400" },
  not_configured: { label: "Yapılandırılmadı", cls: "bg-stone-200 text-stone-600", dot: "bg-stone-400" },
  error: { label: "Bağlantı Hatası", cls: "bg-red-100 text-red-700", dot: "bg-red-500" },
};

const CRED_LABELS = {
  api_key: "API Key", api_secret: "API Secret", supplier_id: "Satıcı (Supplier) ID",
  merchant_id: "Merchant ID", user_id: "Entegrasyon Kullanıcı Adı", integrator_name: "Entegratör Adı (User-Agent)",
  client_id: "Client ID", client_secret: "Client Secret", store_id: "Mağaza (Store) ID",
  chain_id: "Zincir (Chain) ID", vendor_id: "Şube (Vendor) ID",
};

const CRED_LABEL_OVERRIDES = {
  hepsiburada: { merchant_id: "Merchant Id", api_secret: "Secret Key", integrator_name: "Entegratör Adı / User-Agent" },
  n11: { api_key: "App Key", api_secret: "App Secret", integrator_name: "Entegratör Adı" },
  trendyol: { api_key: "App Key", api_secret: "App Secret", integrator_name: "Entegratör Adı / User-Agent" },
  ciceksepeti: { user_id: "Kullanıcı Adı", api_key: "API Anahtarı" },
  shopier: { api_key: "Personal Access Token (PAT)" },
  getir: { api_key: "API Anahtarı", merchant_id: "Mağaza ID" },
  ubereats: { client_id: "Client ID", client_secret: "Client Secret", store_id: "Mağaza (Store) ID" },
  yemeksepeti: { client_id: "Client ID", client_secret: "Client Secret", chain_id: "Zincir (Chain) ID", vendor_id: "Şube (Vendor) ID" },
};

const FALLBACK_CRED_FIELDS = {
  ciceksepeti: ["user_id", "api_key"],
};

const SYNC_PREF_LABELS = [["auto_stock", "Otomatik stok"], ["auto_price", "Otomatik fiyat"], ["auto_orders", "Otomatik sipariş çekme"]];

export default function AdminMarketplaces() {
  const [channels, setChannels] = useState([]);
  const [adapters, setAdapters] = useState([]);
  const [connect, setConnect] = useState(null);
  const [credForm, setCredForm] = useState(null);
  const [edit, setEdit] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newCh, setNewCh] = useState({ name: "", type: "marketplace" });
  const [listingsCh, setListingsCh] = useState(null);
  const [listings, setListings] = useState([]);
  const [products, setProducts] = useState([]);
  const [prodQ, setProdQ] = useState("");
  const [lstForm, setLstForm] = useState(null);
  const [remoteCats, setRemoteCats] = useState(null);
  const [priceEdit, setPriceEdit] = useState(null);
  const [logsCh, setLogsCh] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState("");
  const [ov, setOv] = useState(null);
  const [rep, setRep] = useState(null);

  const load = () => {
    api.get("/admin/marketplaces").then(({ data }) => setChannels(data)).catch(() => {});
    api.get("/admin/marketplaces/adapters").then(({ data }) => setAdapters(data)).catch(() => {});
    api.get("/admin/marketplaces-overview").then(({ data }) => setOv(data)).catch(() => {});
  };
  const loadReports = () => {
    setBusy("reports");
    api.get("/admin/marketplaces-reports").then(({ data }) => setRep(data)).catch(() => toast.error("Raporlar yüklenemedi")).finally(() => setBusy(""));
  };
  useEffect(load, []);

  const adapterOf = (c) => {
    if (c.adapter_key) return adapters.find((a) => a.key === c.adapter_key);
    const norm = (s) => (s || "").toLocaleLowerCase("tr").replace(/[çğıöşü]/g, (m) => ({ "ç": "c", "ğ": "g", "ı": "i", "ö": "o", "ş": "s", "ü": "u" }[m])).replace(/[\s-]/g, "");
    const n = norm(c.name);
    return adapters.find((a) => a.key && n.includes(a.key));
  };

  const openConnect = (c) => {
    setCredForm({
      credentials: {},
      mode: c.mode || "stage",
      commission_rate: c.commission_rate ?? "",
      webhook_secret: "",
      sync_prefs: { auto_stock: true, auto_price: true, auto_orders: true, ...(c.sync_prefs || {}) },
    });
    setConnect(c);
  };

  const saveConnect = async () => {
    setBusy("connect");
    try {
      const payload = { mode: credForm.mode, sync_prefs: credForm.sync_prefs, credentials: Object.fromEntries(Object.entries(credForm.credentials).filter(([, v]) => v)) };
      if (credForm.commission_rate !== "" && credForm.commission_rate !== null) payload.commission_rate = parseFloat(credForm.commission_rate);
      if (credForm.webhook_secret) payload.webhook_secret = credForm.webhook_secret;
      await api.put(`/admin/marketplaces/${connect.channel_id}`, payload);
      toast.success("Bağlantı bilgileri kaydedildi");
      setConnect(null);
      load();
    } catch (e) { toast.error(errText(e) || "Kaydedilemedi"); }
    finally { setBusy(""); }
  };

  const test = async (c) => {
    setBusy(`test-${c.channel_id}`);
    try {
      const { data } = await api.post(`/admin/marketplaces/${c.channel_id}/test`);
      if (data.ok) toast.success(data.message);
      else toast.warning(data.message);
      load();
    } catch { toast.error("Bağlantı testi başarısız"); }
    finally { setBusy(""); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/marketplaces/${edit.channel_id}`, { name: edit.name, logo: edit.logo, is_active: edit.is_active });
      toast.success("Kanal güncellendi");
      setEdit(null);
      load();
    } catch (e) { toast.error(errText(e) || "Güncellenemedi"); }
  };

  const del = async (c) => {
    if (!window.confirm(`"${c.name}" kanalı ve ilan eşleştirmeleri silinsin mi?`)) return;
    try {
      await api.delete(`/admin/marketplaces/${c.channel_id}`);
      toast.success("Kanal silindi");
      load();
    } catch { toast.error("Silinemedi"); }
  };

  const createChannel = async () => {
    if (!newCh.name.trim()) return;
    try {
      await api.post("/admin/marketplaces", newCh);
      toast.success("Kanal eklendi");
      setCreateOpen(false);
      setNewCh({ name: "", type: "marketplace" });
      load();
    } catch (e) { toast.error(errText(e) || "Eklenemedi"); }
  };

  const channelAction = async (c, path, buildMsg) => {
    setBusy(`${path}-${c.channel_id}`);
    try {
      const { data } = await api.post(`/admin/marketplaces/${c.channel_id}/${path}`, {});
      if (data.ok === false) toast.warning(buildMsg(data));
      else toast.success(buildMsg(data));
      load();
      if (listingsCh?.channel_id === c.channel_id) openListings(c);
    } catch (e) { toast.error(errText(e) || "İşlem başarısız"); }
    finally { setBusy(""); }
  };

  const openListings = async (c) => {
    setListingsCh(c);
    setLstForm(null);
    setProdQ("");
    setRemoteCats(null);
    try {
      const [{ data: l }, { data: pr }] = await Promise.all([
        api.get(`/admin/marketplaces/${c.channel_id}/listings`),
        api.get("/admin/products"),
      ]);
      setListings(l);
      setProducts((Array.isArray(pr) ? pr : pr.items || []).filter((p) => p.is_active !== false));
    } catch { toast.error("İlanlar yüklenemedi"); }
  };

  const saveListing = async () => {
    if (!lstForm?.product_id) { toast.warning("Ürün seçin"); return; }
    setBusy("listing");
    try {
      const body = {
        product_id: lstForm.product_id,
        remote_barcode: lstForm.remote_barcode || "",
        remote_sku: lstForm.remote_sku || "",
        remote_category_id: lstForm.remote_category_id || "",
        price_override: lstForm.price_override ? parseFloat(lstForm.price_override) : null,
      };
      if (lstForm.listing_id) await api.put(`/admin/marketplace-listings/${lstForm.listing_id}`, body);
      else await api.post(`/admin/marketplaces/${listingsCh.channel_id}/listings`, body);
      toast.success(lstForm.listing_id ? "Eşleştirme güncellendi" : "Ürün eşleştirildi");
      openListings(listingsCh);
    } catch (e) { toast.error(errText(e) || "Kaydedilemedi"); }
    finally { setBusy(""); }
  };

  const savePrice = async (l, val) => {
    setBusy(`price-${l.listing_id}`);
    try {
      await api.put(`/admin/marketplace-listings/${l.listing_id}`, {
        product_id: l.product_id,
        remote_barcode: l.remote_barcode || "",
        remote_sku: l.remote_sku || "",
        remote_category_id: l.remote_category_id || "",
        price_override: val ? parseFloat(val) : null,
      });
      toast.success("Pazaryeri satış fiyatı kaydedildi — 'Stok/Fiyat Eşitle' ile kanala gönderilir");
      setPriceEdit(null);
      openListings(listingsCh);
    } catch (e) { toast.error(errText(e) || "Kaydedilemedi"); }
    finally { setBusy(""); }
  };

  const delListing = async (l) => {
    if (!window.confirm("Bu ilan eşleştirmesi silinsin mi?")) return;
    try {
      await api.delete(`/admin/marketplace-listings/${l.listing_id}`);
      toast.success("Eşleştirme silindi");
      openListings(listingsCh);
    } catch { toast.error("Silinemedi"); }
  };

  const fetchRemoteCats = async () => {
    setBusy("cats");
    try {
      const { data } = await api.get(`/admin/marketplaces/${listingsCh.channel_id}/remote-categories`);
      setRemoteCats(data.categories || []);
      toast.success(`${data.count} kategori çekildi`);
    } catch (e) { toast.error(errText(e) || "Kategoriler çekilemedi"); }
    finally { setBusy(""); }
  };

  const openLogs = async (c) => {
    setLogsCh(c);
    try {
      const { data } = await api.get(`/admin/marketplaces/${c.channel_id}/sync-logs`);
      setLogs(data);
    } catch { toast.error("Kayıtlar yüklenemedi"); }
  };

  const clearLogs = async () => {
    if (!window.confirm("Bu kanalın tüm senkron kayıtları silinsin mi?")) return;
    await api.delete(`/admin/marketplaces/${logsCh.channel_id}/sync-logs`);
    toast.success("Kayıtlar temizlendi");
    openLogs(logsCh);
  };

  const filteredProducts = products.filter((p) => !prodQ || (p.name + (p.sku || "") + (p.barcode || "")).toLowerCase().includes(prodQ.toLowerCase())).slice(0, 50);
  const connectAdapter = connect ? adapterOf(connect) : null;

  return (
    <div data-testid="admin-marketplaces-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-forest">Pazaryeri ve Kanal Yönetimi</h1>
          <p className="text-stone-500 text-sm mt-1">Tüm satış kanallarını tek merkezden yönetin. Merkezi ürün ve stok veritabanı tek gerçek kaynaktır.</p>
        </div>
        <button data-testid="add-channel-button" onClick={() => setCreateOpen(true)} className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"><Plus className="w-4 h-4" /> Yeni Kanal</button>
      </div>

      {ov && (
        <div data-testid="omnichannel-center" className="mt-5 bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-semibold text-stone-900 flex items-center gap-2"><LayoutDashboard className="w-4 h-4 text-forest" /> Omnichannel Center</h2>
            <div className="flex gap-4 text-xs">
              <span data-testid="ov-today-orders" className="text-stone-600">Bugünkü Sipariş: <b className="text-forest">{ov.totals.today_orders}</b></span>
              <span data-testid="ov-today-revenue" className="text-stone-600">Bugünkü Ciro: <b className="text-forest">{tl(ov.totals.today_revenue)}</b></span>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap mt-3">
            {ov.channels.map((o) => (
              <Link key={o.channel_id} to={`/admin/pazaryerleri/${o.channel_id}`} data-testid={`ov-channel-${o.channel_id}`} className="flex items-center gap-2 border border-stone-200 hover:border-forest rounded-xl px-3.5 py-2.5 transition-colors">
                <span className={`w-2.5 h-2.5 rounded-full ${{ green: "bg-emerald-500", yellow: "bg-amber-400", gray: "bg-stone-300" }[o.health]}`} />
                <span className="text-xs font-semibold text-stone-800">{o.name}</span>
                <span className="text-[10px] text-stone-400">{o.today_orders} sipariş · {tl(o.today_revenue)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {!rep && (
        <div className="mt-5 bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm text-center">
          <button data-testid="load-reports-btn" onClick={loadReports} disabled={busy === "reports"} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-forest hover:bg-forest-dark text-white text-sm font-semibold transition-colors disabled:bg-stone-400">
            <BarChart3 className="w-4 h-4" /> {busy === "reports" ? "Raporlar yükleniyor..." : "Omnichannel Raporları Yükle"}
          </button>
          <p className="text-[11px] text-stone-400 mt-2">Raporlar tüm kanal siparişlerini taradığı için yalnızca ihtiyaç duyduğunuzda yükleyin.</p>
        </div>
      )}
      {rep && (
        <div data-testid="omnichannel-reports" className="mt-5 bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-semibold text-stone-900">Omnichannel Raporlar</h2>
            <div className="flex gap-4 text-xs text-stone-600 flex-wrap">
              <span data-testid="rep-total-orders">Toplam Sipariş: <b className="text-forest">{rep.totals.orders}</b></span>
              <span data-testid="rep-total-revenue">Toplam Ciro: <b className="text-forest">{tl(rep.totals.revenue)}</b></span>
              <span>İptal: <b>{rep.totals.cancelled}</b></span>
              <span>İade: <b>{rep.totals.returns}</b></span>
              {rep.best_channel && <span data-testid="rep-best-channel">En İyi Kanal: <b className="text-gold-dark">{rep.best_channel.name}</b> ({tl(rep.best_channel.revenue)})</span>}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]" data-testid="channel-comparison-table">
              <thead className="bg-stone-50 text-stone-500 text-xs uppercase">
                <tr><th className="text-left px-3 py-2">Kanal</th><th className="text-right px-3 py-2">Sipariş</th><th className="text-right px-3 py-2">Ciro</th><th className="text-right px-3 py-2">İptal</th><th className="text-right px-3 py-2">İade</th><th className="text-right px-3 py-2">Eşleşen Ürün</th></tr>
              </thead>
              <tbody>
                {rep.channels.map((r) => (
                  <tr key={r.channel_id} data-testid={`rep-row-${r.channel_id}`} className="border-t border-stone-100">
                    <td className="px-3 py-2 font-medium text-stone-800">{r.name}</td>
                    <td className="px-3 py-2 text-right">{r.orders}</td>
                    <td className="px-3 py-2 text-right font-semibold text-forest">{tl(r.revenue)}</td>
                    <td className="px-3 py-2 text-right">{r.cancelled}</td>
                    <td className="px-3 py-2 text-right">{r.returns}</td>
                    <td className="px-3 py-2 text-right">{r.product_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rep.top_products.length > 0 && (
            <p className="text-xs text-stone-500" data-testid="rep-top-products">Kanal satışlarında en çok satanlar: {rep.top_products.map((p) => `${p.name} (${p.qty})`).join(", ")}</p>
          )}
          {rep.critical_stock.length > 0 && (
            <div data-testid="critical-stock-table">
              <h3 className="text-sm font-semibold text-red-700 mb-2">Kritik Stok (kanal riski)</h3>
              <div className="space-y-1">
                {rep.critical_stock.slice(0, 8).map((p) => (
                  <div key={p.product_id} data-testid={`critical-stock-${p.product_id}`} className="flex items-center gap-3 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    <span className="flex-1 font-medium text-stone-800">{p.name}</span>
                    <span>ATA Stok: <b>{fmtQty4(p.stock, p.base_unit || p.unit || "")}</b></span>
                    <span>Kanal sayısı: <b>{p.channel_count}</b></span>
                    <span className={`font-bold ${p.risk === "yüksek" ? "text-red-600" : p.risk === "orta" ? "text-amber-600" : "text-stone-500"}`}>Risk: {p.risk}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {[["marketplace", "Pazaryerleri"], ["quick_delivery", "Hızlı Alışveriş"], ["custom", "Özel Kanallar"]].map(([gt, gl]) => {
        const gch = (channels || []).filter((c) => (c.type || "marketplace") === gt);
        if (!gch.length) return null;
        return (
          <div key={gt} className="mt-6">
            <h3 data-testid={`channel-group-${gt}`} className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">{gl}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {gch.map((c) => {
          const ui = STATUS_UI[c.status] || STATUS_UI.not_configured;
          const inactive = c.is_active === false;
          const adp = adapterOf(c);
          return (
            <div key={c.channel_id} data-testid={`marketplace-${c.name.toLowerCase().replace(/[^a-z0-9]/g, "")}-card`} className={`bg-white rounded-2xl border border-stone-200/80 p-5 shadow-sm ${inactive ? "opacity-55" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {c.logo ? (
                    <img src={c.logo} alt={c.name} className="w-10 h-10 rounded-xl object-cover border border-stone-200" />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-forest/10 flex items-center justify-center"><Store className="w-5 h-5 text-forest" /></div>
                  )}
                  <div>
                    <p className="font-bold text-stone-900">{c.name}{inactive && <span className="ml-2 text-[10px] font-semibold text-stone-400 uppercase">(pasif)</span>}</p>
                    <p className="text-[10px] uppercase tracking-wider text-stone-400">{{ marketplace: "Pazaryeri", quick_delivery: "Hızlı Teslimat", custom: "Özel Kanal" }[c.type] || c.type}{adp ? ` · ${adp.label} adaptörü` : ""}</p>
                  </div>
                </div>
                <span className={`w-2.5 h-2.5 rounded-full ${ui.dot}`} />
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${ui.cls}`}>{inactive ? "Pasif" : ui.label}</span>
                {adp && <span data-testid={`marketplace-mode-${c.channel_id}`} className={`text-[10px] font-bold px-2.5 py-1.5 rounded-full ${c.mode === "live" ? "bg-forest text-white" : "bg-amber-100 text-amber-800"}`}>{c.mode === "live" ? "CANLI" : "TEST (STAGE)"}</span>}
              </div>
              <dl className="mt-3 space-y-1 text-xs text-stone-500">
                <div className="flex justify-between"><dt>Son senkronizasyon</dt><dd>{c.last_sync ? fmtDate(c.last_sync) : "—"}</dd></div>
                <div className="flex justify-between"><dt>Bağlı ürün</dt><dd>{c.product_count}</dd></div>
                <div className="flex justify-between"><dt>Bekleyen sipariş</dt><dd>{c.pending_orders}</dd></div>
                <div className="flex justify-between"><dt>Komisyon</dt><dd>{c.commission_rate != null ? `%${c.commission_rate}` : "—"}</dd></div>
              </dl>
              {c.last_error && <p className="mt-2 text-xs text-terra flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5 shrink-0" /> {c.last_error}</p>}
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Link to={`/admin/pazaryerleri/${c.channel_id}`} data-testid={`marketplace-detail-${c.channel_id}`} className="col-span-2 flex items-center justify-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white text-xs font-semibold py-2.5 rounded-lg transition-colors"><LayoutDashboard className="w-3.5 h-3.5" /> Kanal Detayı (9 Sekme)</Link>
                <button data-testid={`marketplace-connect-${c.channel_id}`} onClick={() => openConnect(c)} className="flex items-center justify-center gap-1.5 bg-forest hover:bg-forest-dark text-white text-xs font-semibold py-2.5 rounded-lg transition-colors"><Plug className="w-3.5 h-3.5" /> Bağlan</button>
                <button data-testid={`marketplace-test-${c.channel_id}`} disabled={busy === `test-${c.channel_id}`} onClick={() => test(c)} className="border border-stone-300 hover:border-forest text-stone-700 text-xs font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50">{busy === `test-${c.channel_id}` ? "Test ediliyor..." : "Bağlantıyı Test Et"}</button>
                <button data-testid={`marketplace-listings-${c.channel_id}`} onClick={() => openListings(c)} className="flex items-center justify-center gap-1.5 border border-stone-300 hover:border-forest text-stone-700 text-xs font-semibold py-2.5 rounded-lg transition-colors"><Package className="w-3.5 h-3.5" /> İlanlar</button>
                <button data-testid={`marketplace-logs-${c.channel_id}`} onClick={() => openLogs(c)} className="flex items-center justify-center gap-1.5 border border-stone-300 hover:border-forest text-stone-700 text-xs font-semibold py-2.5 rounded-lg transition-colors"><ScrollText className="w-3.5 h-3.5" /> Kayıtlar</button>
                <button data-testid={`marketplace-edit-${c.channel_id}`} onClick={() => setEdit({ ...c, is_active: c.is_active !== false })} className="flex items-center justify-center gap-1.5 border border-stone-300 hover:border-forest text-stone-700 text-xs font-semibold py-2.5 rounded-lg transition-colors"><Pencil className="w-3.5 h-3.5" /> Düzenle</button>
                <button data-testid={`marketplace-delete-${c.channel_id}`} onClick={() => del(c)} className="flex items-center justify-center gap-1.5 border border-red-200 hover:bg-red-50 text-terra text-xs font-semibold py-2.5 rounded-lg transition-colors"><Trash2 className="w-3.5 h-3.5" /> Sil</button>
              </div>
              {adp && (
                <div className="mt-2 space-y-2">
                  <button data-testid={`marketplace-sync-${c.channel_id}`} disabled={busy === `sync-stock-price-${c.channel_id}`} onClick={() => channelAction(c, "sync-stock-price", (d) => `${d.items} ürünün stok/fiyatı gönderildi`)} className="w-full flex items-center justify-center gap-1.5 bg-gold hover:bg-gold-dark text-stone-900 text-xs font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50"><RefreshCw className="w-3.5 h-3.5" /> Stok/Fiyat Eşitle</button>
                  <div className="grid grid-cols-3 gap-2">
                    <button data-testid={`marketplace-push-${c.channel_id}`} disabled={busy === `push-listings-${c.channel_id}`} onClick={() => channelAction(c, "push-listings", (d) => `${d.pushed} ürün gönderildi${d.failed ? `, ${d.failed} hatalı` : ""}`)} className="flex items-center justify-center gap-1 border border-stone-300 hover:border-forest text-stone-700 text-[11px] font-semibold py-2 rounded-lg transition-colors disabled:opacity-50"><Upload className="w-3 h-3" /> Ürün Gönder</button>
                    <button data-testid={`marketplace-pull-orders-${c.channel_id}`} disabled={busy === `pull-orders-${c.channel_id}`} onClick={() => channelAction(c, "pull-orders", (d) => `${d.fetched} paket: ${d.imported} yeni, ${d.skipped} atlandı`)} className="flex items-center justify-center gap-1 border border-stone-300 hover:border-forest text-stone-700 text-[11px] font-semibold py-2 rounded-lg transition-colors disabled:opacity-50"><ShoppingCart className="w-3 h-3" /> Sipariş Çek</button>
                    <button data-testid={`marketplace-pull-claims-${c.channel_id}`} disabled={busy === `pull-claims-${c.channel_id}`} onClick={() => channelAction(c, "pull-claims", (d) => `${d.fetched} iade kaydı, ${d.new} yeni`)} className="flex items-center justify-center gap-1 border border-stone-300 hover:border-forest text-stone-700 text-[11px] font-semibold py-2 rounded-lg transition-colors disabled:opacity-50"><RotateCcw className="w-3 h-3" /> İade Çek</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
            </div>
          </div>
        );
      })}

      <div className="mt-6 bg-cream-dark/60 border border-stone-200 rounded-2xl p-5 text-sm text-stone-600">
        <p className="font-semibold text-stone-800">Nasıl çalışır?</p>
        <p className="mt-1">Trendyol, Hepsiburada ve N11 adaptörleri hazır. "Bağlan" ile geliştirici hesabınızdaki anahtarları girin, "Bağlantıyı Test Et" ile doğrulayın. "İlanlar"dan ürün eşleştirmesi yapın; stok değişimleri otomatik, toplu eşitleme "Stok/Fiyat Eşitle" ile gönderilir. Siparişler webhook + 5 dakikalık otomatik çekme ile siparişlere düşer, komisyon ve hakediş muhasebeye çift taraflı işlenir.</p>
      </div>

      <Dialog open={!!connect} onOpenChange={() => setConnect(null)}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-forest" /> {connect?.name} API Bağlantısı</DialogTitle></DialogHeader>
          {connect && credForm && (
            <div className="space-y-4 mt-2">
              {!connectAdapter && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">Bu kanal için adaptör tanımlı değil — gerçek API bağlantısı Trendyol, Hepsiburada ve N11 için hazırdır. Bilgiler yine de saklanır.</p>
              )}
              {(connectAdapter ? connectAdapter.credential_fields : (FALLBACK_CRED_FIELDS[connect.adapter_key] || ["api_key", "api_secret", "merchant_id"])).map((f) => {
                const flabel = CRED_LABEL_OVERRIDES[connect.adapter_key]?.[f] || CRED_LABELS[f] || f;
                return (
                <div key={f}>
                  <label className={lbl}>{flabel}</label>
                  <input data-testid={`mp-cred-${f}`} type={f.includes("secret") || f === "api_key" ? "password" : "text"} className={`${inputCls} mt-1`} placeholder={connect.credentials?.[`${f}_set`] || connect.credentials?.[`${f}_masked`] ? "Kayıtlı — değiştirmek için yeni değer girin" : flabel} value={credForm.credentials[f] || ""} onChange={(e) => setCredForm({ ...credForm, credentials: { ...credForm.credentials, [f]: e.target.value } })} />
                </div>
                );
              })}
              <div>
                <label className={lbl}>Ortam</label>
                <select data-testid="mp-mode" className={`${inputCls} mt-1`} value={credForm.mode} onChange={(e) => setCredForm({ ...credForm, mode: e.target.value })}>
                  <option value="stage">Test (Stage/Sandbox)</option>
                  <option value="live">Canlı (Production)</option>
                </select>
              </div>
              <div>
                <label className={lbl}>Komisyon Oranı % (hakediş muhasebesi için)</label>
                <input data-testid="mp-commission" type="number" min="0" step="0.01" className={`${inputCls} mt-1`} value={credForm.commission_rate} onChange={(e) => setCredForm({ ...credForm, commission_rate: e.target.value })} placeholder="örn. 18" />
              </div>
              {connectAdapter?.supports_webhook && (
                <div>
                  <label className={lbl}>Webhook Secret (sipariş bildirimleri için)</label>
                  <input data-testid="mp-webhook-secret" type="password" className={`${inputCls} mt-1`} value={credForm.webhook_secret} onChange={(e) => setCredForm({ ...credForm, webhook_secret: e.target.value })} placeholder={connect.webhook_secret ? "Kayıtlı — değiştirmek için yeni değer girin" : "Güvenli bir anahtar belirleyin"} />
                  <p className="text-[11px] text-stone-400 mt-1 break-all">Webhook adresi: {window.location.origin}/api/webhooks/marketplace/{connectAdapter.key} — pazaryeri paneline bu adresi ve secret'ı tanımlayın.</p>
                </div>
              )}
              <div className="bg-cream-dark/60 rounded-xl p-3.5 space-y-2">
                <p className={lbl}>Otomatik Senkronizasyon</p>
                {SYNC_PREF_LABELS.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                    <input data-testid={`mp-sync-${k}`} type="checkbox" className="w-4 h-4 accent-forest" checked={!!credForm.sync_prefs[k]} onChange={(e) => setCredForm({ ...credForm, sync_prefs: { ...credForm.sync_prefs, [k]: e.target.checked } })} /> {label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-stone-400">Boş bırakılan alanlar mevcut değeri korur. Anahtarlar güvenli saklanır ve arayüzde gösterilmez.</p>
              <button data-testid="mp-save" onClick={saveConnect} disabled={busy === "connect"} className="w-full bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white font-semibold py-3 rounded-xl transition-colors">{busy === "connect" ? "Kaydediliyor..." : "Kaydet"}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!listingsCh} onOpenChange={() => setListingsCh(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-forest" /> {listingsCh?.name} — Ürün Eşleştirme ({listings.length})</DialogTitle></DialogHeader>
          {listingsCh && (
            <div className="space-y-4 mt-2">
              <div className="bg-cream-dark/60 rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-stone-600">{lstForm?.listing_id ? "Eşleştirmeyi Düzenle" : "Yeni Ürün Eşleştir"}</p>
                <input data-testid="listing-product-q" className={inputCls} placeholder="Ürün ara (ad, SKU, barkod)..." value={prodQ} onChange={(e) => setProdQ(e.target.value)} />
                <select data-testid="listing-product-select" className={inputCls} value={lstForm?.product_id || ""} onChange={(e) => { const p = products.find((x) => x.product_id === e.target.value); setLstForm({ ...(lstForm || {}), product_id: e.target.value, remote_barcode: lstForm?.remote_barcode ?? p?.barcode ?? "", remote_sku: lstForm?.remote_sku ?? p?.sku ?? "" }); }}>
                  <option value="">— Ürün seçin —</option>
                  {filteredProducts.map((p) => <option key={p.product_id} value={p.product_id}>{p.name} ({p.sku}) — stok {fmtQty4(p.stock, p.base_unit || p.unit || "")}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="listing-remote-barcode" className={inputCls} placeholder="Pazaryeri barkodu" value={lstForm?.remote_barcode || ""} onChange={(e) => setLstForm({ ...(lstForm || {}), remote_barcode: e.target.value })} />
                  <input data-testid="listing-remote-sku" className={inputCls} placeholder="Pazaryeri SKU / stok kodu" value={lstForm?.remote_sku || ""} onChange={(e) => setLstForm({ ...(lstForm || {}), remote_sku: e.target.value })} />
                  <input data-testid="listing-remote-category" className={inputCls} placeholder="Pazaryeri kategori ID" value={lstForm?.remote_category_id || ""} onChange={(e) => setLstForm({ ...(lstForm || {}), remote_category_id: e.target.value })} />
                  <input data-testid="listing-price-override" type="number" step="0.01" className={inputCls} placeholder="Fiyat override (ops.)" value={lstForm?.price_override || ""} onChange={(e) => setLstForm({ ...(lstForm || {}), price_override: e.target.value })} />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button data-testid="listing-save" onClick={saveListing} disabled={busy === "listing"} className="bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors">{busy === "listing" ? "Kaydediliyor..." : lstForm?.listing_id ? "Güncelle" : "Eşleştir"}</button>
                  {lstForm?.listing_id && <button onClick={() => setLstForm(null)} className="text-xs text-stone-500 font-semibold px-3 py-2">Vazgeç</button>}
                  <button data-testid="listing-fetch-categories" onClick={fetchRemoteCats} disabled={busy === "cats"} className="ml-auto text-xs border border-stone-300 hover:border-forest text-stone-600 font-semibold px-3 py-2.5 rounded-lg transition-colors disabled:opacity-50">{busy === "cats" ? "Çekiliyor..." : "Pazaryeri Kategorilerini Çek"}</button>
                </div>
                {remoteCats && (
                  <select data-testid="listing-remote-cat-select" className={inputCls} value="" onChange={(e) => e.target.value && setLstForm({ ...(lstForm || {}), remote_category_id: e.target.value })}>
                    <option value="">— Kategori seç ({remoteCats.length}) —</option>
                    {remoteCats.slice(0, 500).map((ct) => <option key={ct.categoryId || ct.id} value={ct.categoryId || ct.id}>{ct.name || ct.categoryName}</option>)}
                  </select>
                )}
              </div>
              {listings.length === 0 && <p className="text-sm text-stone-500">Henüz eşleştirilmiş ürün yok.</p>}
              <div className="divide-y divide-stone-100">
                {listings.map((l) => (
                  <div key={l.listing_id} data-testid={`listing-row-${l.listing_id}`} className="py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-stone-800 truncate">{l.product?.name || l.product_id}</p>
                        <p className="text-[11px] text-stone-400">barkod: {l.remote_barcode || "—"} · sku: {l.remote_sku || "—"} · kat: {l.remote_category_id || "—"} · site: {tl(l.product?.discount_price || l.product?.price || 0)} ₺{l.price_override ? ` · pazaryeri: ${tl(l.price_override)} ₺` : ""}</p>
                        <p className="text-[11px] mt-0.5">
                          <span className={`font-semibold ${{ synced: "text-emerald-600", pending: "text-amber-600", error: "text-terra" }[l.sync_status] || "text-stone-500"}`}>{{ synced: "Senkron", pending: "Bekliyor", error: "Hata" }[l.sync_status] || l.sync_status}</span>
                          {l.last_pushed_stock != null && <span className="text-stone-400"> · son stok: {fmtQty4(l.last_pushed_stock)}</span>}
                          {l.last_error && <span className="text-terra"> · {l.last_error}</span>}
                        </p>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        <button data-testid={`listing-price-${l.listing_id}`} onClick={() => setPriceEdit(priceEdit?.id === l.listing_id ? null : { id: l.listing_id, val: l.price_override ?? l.product?.discount_price ?? l.product?.price ?? "" })} className="p-2 border border-stone-300 hover:border-forest rounded-lg text-stone-600"><Tag className="w-3.5 h-3.5" /></button>
                        <button data-testid={`listing-edit-${l.listing_id}`} onClick={() => setLstForm({ ...l, price_override: l.price_override ?? "" })} className="p-2 border border-stone-300 hover:border-forest rounded-lg text-stone-600"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`listing-del-${l.listing_id}`} onClick={() => delListing(l)} className="p-2 border border-red-200 hover:bg-red-50 rounded-lg text-terra"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                    {priceEdit?.id === l.listing_id && (
                      <div data-testid={`listing-price-panel-${l.listing_id}`} className="mt-2 bg-cream-dark/60 rounded-xl p-3 flex items-end gap-3 flex-wrap">
                        <div><p className="text-[11px] text-stone-500 font-semibold">SİTE SATIŞ FİYATI</p><p className="text-sm font-bold text-stone-800">{tl(l.product?.discount_price || l.product?.price || 0)} ₺</p></div>
                        <div><p className="text-[11px] text-stone-500 font-semibold">STOK</p><p className="text-sm font-bold text-stone-800">{l.product?.stock ?? "—"}</p></div>
                        <div className="flex-1 min-w-[140px]"><label className="text-[11px] text-stone-500 font-semibold block mb-1">PAZARYERİ SATIŞ FİYATI ₺</label>
                          <input data-testid={`listing-price-input-${l.listing_id}`} type="number" step="0.01" min="0" className={inputCls} value={priceEdit.val} onChange={(e) => setPriceEdit({ ...priceEdit, val: e.target.value })} /></div>
                        <button data-testid={`listing-price-save-${l.listing_id}`} onClick={() => savePrice(l, priceEdit.val)} disabled={busy === `price-${l.listing_id}`} className="bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors">{busy === `price-${l.listing_id}` ? "Kaydediliyor..." : "Kaydet"}</button>
                        <p className="w-full text-[11px] text-stone-400">Boş bırakılırsa site fiyatı kullanılır. Kaydettikten sonra "Stok/Fiyat Eşitle" butonuyla pazaryerine gönderilir.</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!logsCh} onOpenChange={() => setLogsCh(null)}>
        <DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ScrollText className="w-5 h-5 text-forest" /> {logsCh?.name} — Senkron Kayıtları</DialogTitle></DialogHeader>
          <div className="mt-2 space-y-2">
            {logs.length === 0 && <p className="text-sm text-stone-500">Henüz kayıt yok.</p>}
            {logs.map((lg) => (
              <div key={lg.log_id} className="flex items-start gap-2.5 text-xs border-b border-stone-100 pb-2">
                <span className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${lg.ok ? "bg-emerald-500" : "bg-red-500"}`} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-stone-700">{lg.action}</p>
                  <p className="text-stone-500 break-words">{lg.message}</p>
                </div>
                <span className="text-stone-400 shrink-0">{fmtDate(lg.created_at)}</span>
              </div>
            ))}
            {logs.length > 0 && <button data-testid="logs-clear" onClick={clearLogs} className="text-xs text-terra font-semibold">Tümünü Temizle</button>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Kanal Düzenle — {edit?.name}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4 mt-2">
              <div><label className={lbl}>Kanal Adı</label><input data-testid="channel-edit-name" className={`${inputCls} mt-1`} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
              <div>
                <label className={lbl}>Kanal Logosu</label>
                <div className="flex items-center gap-3 mt-1">
                  {edit.logo ? <img src={edit.logo} alt="" className="w-12 h-12 rounded-xl object-cover border border-stone-200" /> : <div className="w-12 h-12 rounded-xl bg-cream-dark flex items-center justify-center"><Store className="w-5 h-5 text-stone-400" /></div>}
                  <MediaPicker type="image" testid="channel-logo-picker" label="Kütüphaneden Seç" onSelect={(url) => setEdit({ ...edit, logo: url })} />
                  {edit.logo && <button type="button" onClick={() => setEdit({ ...edit, logo: "" })} className="text-xs text-terra font-semibold">Kaldır</button>}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input data-testid="channel-edit-active" type="checkbox" className="w-4 h-4 accent-forest" checked={edit.is_active} onChange={(e) => setEdit({ ...edit, is_active: e.target.checked })} /> Kanal aktif
              </label>
              <button data-testid="channel-edit-save" onClick={saveEdit} className="w-full bg-forest hover:bg-forest-dark text-white font-semibold py-3 rounded-xl transition-colors">Kaydet</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Yeni Kanal Ekle</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <input data-testid="channel-new-name" className={inputCls} placeholder="Kanal adı (örn. Trendyol Go)" value={newCh.name} onChange={(e) => setNewCh({ ...newCh, name: e.target.value })} />
            <select data-testid="channel-new-type" className={inputCls} value={newCh.type} onChange={(e) => setNewCh({ ...newCh, type: e.target.value })}>
              <option value="marketplace">Pazaryeri</option>
              <option value="quick_delivery">Hızlı Teslimat</option>
              <option value="custom">Özel Kanal</option>
            </select>
            <button data-testid="channel-create-save" onClick={createChannel} className="w-full bg-forest hover:bg-forest-dark text-white font-semibold py-3 rounded-xl transition-colors">Ekle</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
